import React, { useEffect, useState, useRef, useMemo } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Modal, Button, Form, Toast, ToastContainer } from 'react-bootstrap';
import QRCode from 'react-qr-code';
import DataTable from "datatables.net-react";
import DataTablesCore from "datatables.net-bs5";
import "datatables.net-buttons-bs5";
import "datatables.net-buttons/js/buttons.html5.mjs";
import "datatables.net-buttons/js/buttons.print.mjs";
import "datatables.net-responsive-bs5";

DataTable.use(DataTablesCore);

/*
  Filename:    ManageVolunteers.js
  Description: This component allows an organization admin or a root user to view, add, edit, and remove volunteers from their organization.
*/

const ManageVolunteers = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationName, setOrganizationName] = useState('Loading...');
  const [organizationId, setOrganizationId] = useState(null);
  const [isRootUser, setIsRootUser] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userName, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [editVolunteerID, setEditVolunteerID] = useState(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [showEditVolunteerModal, setShowEditVolunteerModal] = useState(false);
  const [showAddVolunteerModal, setShowAddVolunteerModal] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [volunteerToRemove, setVolunteerToRemove] = useState(null);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [notificationHeader, setNotificationHeader] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationType, setNotificationType] = useState('success');

  // State variables for logged-in user
  const [loggedInUserId, setLoggedInUserId] = useState(null);

  // State variables for user identification during Add Volunteer
  const [showIdentifyForm, setShowIdentifyForm] = useState(true);
  const [manualIdentifyForm, setManualIdentifyForm] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userScanCode, setUserScanCode] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUserForVolunteer, setSelectedUserForVolunteer] = useState(null);
  const [userPage, setUserPage] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const searchInputRef = useRef(null);
  const codeInputRef = useRef(null);

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get logged-in user's ID from cookies
  useEffect(() => {
    const userID = Cookies.get("userID");
    if (userID) {
      setLoggedInUserId(userID);
    }
  }, []);

  const handleSubmit = (e) => {
    // Prevent default form submission if it were a standard HTML form submit
    if (e) e.preventDefault();
    handleEditVolunteer(editVolunteerID);
    // Optional: Close the modal after submission (if not handled in handleEditVolunteer)
    // setShowEditVolunteerModal(false);
  };

  const handleEditFirstNameChange = (e) => {
    setEditFirstName(e.target.value);
  };
  const handleEditLastNameChange = (e) => {
    setEditLastName(e.target.value);
  };

  // Reset Add Volunteer Modal states
  const resetAddVolunteerModal = () => {
    setShowIdentifyForm(true);
    setManualIdentifyForm(false);
    setUserSearch("");
    setUserScanCode("");
    setSelectedUserForVolunteer(null);
    setUserPage(0);
    setFirstName("");
    setLastName("");
  };

  // Show notification banner
  const showNotificationBanner_func = (header, body, type) => {
    setNotificationHeader(header);
    setNotificationBody(body);
    setNotificationType(type);
    setShowNotificationBanner(true);
    setTimeout(() => {
      setShowNotificationBanner(false);
    }, 5000);
  };

  // Fetch user/client list for searching
  const fetchUserList = async () => {
    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      return;
    }

    try {
      const response = await fetch(
        process.env.REACT_APP_BACKURL + '/api/foodPantry/client/list',
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionID}`
          },
          method: 'GET'
        }
      );
      const data = await response.json();
      setUserSearchResults(Array.isArray(data.clients) ? data.clients : []);
    } catch (error) {
      console.error("Error fetching user list:", error);
      setUserSearchResults([]);
    }
  };

  // Handle user selection from search or scan
  const handleUserSelect = (user) => {
    setSelectedUserForVolunteer(user);
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setEmail(user.email || "");
    setShowIdentifyForm(false);
  };

  // Handle scan code input for user identification
  const handleUserScanKeyDown = (e) => {
    if (e.key === "Enter" && userScanCode.trim()) {
      const user = userSearchResults.find(u => u.userID === userScanCode);
      if (!user) {
        alert("User not found. Please check the ID or register the user first.");
        return;
      }
      handleUserSelect(user);
      setUserScanCode("");
      e.preventDefault();
    }
  };

  const fetchVolunteers = async (currentOrgName) => {
    if (!currentOrgName || currentOrgName === "Loading..." || currentOrgName === "Select Organization" || currentOrgName === "No Organization Found in cookie" || currentOrgName === "Error loading organization") {
      setVolunteers([]);
      return;
    }

    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      navigate("/SignIn");
      return;
    }

    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKURL + "/api/foodPantry/volunteer",
        {
          params: { orgName: currentOrgName },
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionID}`,
          },
        }
      );
      const data = response.data;
      if (data && Array.isArray(data.Volunteers)) {
        setVolunteers(data.Volunteers);
      } else {
        console.error("Invalid format: ", data);
        setVolunteers([]);
      }
    } catch (error) {
      console.error("Error fetching volunteers: ", error);
      setVolunteers([]);
    }
  };


  // Add the volunteer
  const handleAddVolunteer = async () => {
    if (!selectedUserForVolunteer || !selectedUserForVolunteer.userID) {
      alert("Please select a user first.");
      return;
    }

    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      alert("Session expired. Please sign in again.");
      navigate("/SignIn");
      return;
    }

    // Get organization ID
    let activeOrgID = organizationId;
    if (!activeOrgID && isRootUser) {
      const selectedOrg = organizations.find(org => org.name === organizationName);
      activeOrgID = selectedOrg?.id;
    }

    if (!activeOrgID) {
      alert("Organization ID not found. Please select an organization first.");
      return;
    }

    const newVolunteer = {
      volunteer: {
        userID: selectedUserForVolunteer.userID,
        isActive: true,
        activeOrgID: activeOrgID,
        isDonor: false
      }
    };

    try {
      await axios.post(
        process.env.REACT_APP_BACKURL + "/api/foodPantry/volunteer",
        newVolunteer,
        {
          headers: {
            Authorization: `Bearer ${sessionID}`,
          },
          withCredentials: true,
        }
      );

      // Update OrgID cookie if volunteer is being clocked in
      if (newVolunteer.volunteer.isActive && activeOrgID) {
        Cookies.set('OrgID', activeOrgID, {
          expires: 0.5, // 12 hours
          secure: false,
          sameSite: 'Strict'
        });
      }

      await fetchVolunteers(organizationName);

      showNotificationBanner_func('Success', `Successfully checked in ${selectedUserForVolunteer.firstName} ${selectedUserForVolunteer.lastName}`, 'success');

      // Reset form fields
      resetAddVolunteerModal();
      setShowAddVolunteerModal(false);

    } catch (error) {
      console.error("Error adding volunteer: ", error);
      const errorMsg = error.response?.data?.message || "Error adding volunteer. Please try again.";
      showNotificationBanner_func('Error', errorMsg, 'error');
    }
  };

  // Helper function to handle 'Enter' key press for Add Volunteer modal
  const handleAddKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Prevent default form submission behavior (if any)
      handleAddVolunteer();
    }
  };

  // Edit the volunteer
  const handleEditVolunteer = async (volunteerID) => {
    // Ensure the volunteerID is present before proceeding
    if (!volunteerID) {
      alert("Error: Volunteer ID not found for editing.");
      return;
    }

    if (!editFirstName || !editLastName) {
      alert("Please fill in all fields.");
      return;
    }

    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      alert("Session expired. Please sign in again.");
      navigate("/SignIn");
      return;
    }

    const data = {
      volunteerID,
      orgName: organizationName,
      firstName: editFirstName,
      lastName: editLastName,
      photo: null,
      roleID: null,
    };
    try {
      await axios.put(
        process.env.REACT_APP_BACKURL + "/api/foodPantry/volunteer",
        data,
        {
          headers: {
            Authorization: `Bearer ${sessionID}`,
          },
          withCredentials: true,
        }
      );
      // Refresh the volunteers
      setVolunteers((prevVolunteers) =>
        prevVolunteers.map((volunteer) =>
          volunteer.userID === volunteerID
            ? { ...volunteer, firstName: editFirstName, lastName: editLastName }
            : volunteer
        )
      );
      // Reset form fields
      setEditFirstName("");
      setEditLastName("");

      setShowEditVolunteerModal(false);

    } catch (error) {
      console.error("Error editing volunteer: ", error);
      alert("Error editing volunteer. Please try again.");
    }
  };

  // Helper function to handle 'Enter' key press for Edit Volunteer modal
  const handleEditKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Prevent default form submission behavior (if any)
      handleEditVolunteer(editVolunteerID);
    }
  };

  // Show remove confirmation modal
  const handleRemove = (volunteerID) => {
    if (!volunteerID) {
      alert("Error: Volunteer ID not found for removal.");
      return;
    }
    const volunteer = volunteers.find(v => v.userID === volunteerID);
    setVolunteerToRemove(volunteer);
    setShowRemoveConfirmModal(true);
  };

  // Confirm and execute volunteer removal
  const handleConfirmRemove = async () => {
    if (!volunteerToRemove || !volunteerToRemove.userID) {
      alert("Error: Volunteer ID not found for removal.");
      return;
    }

    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      alert("Session expired. Please sign in again.");
      navigate("/SignIn");
      return;
    }

    try {
      await axios.delete(
        process.env.REACT_APP_BACKURL + "/api/foodPantry/volunteer",
        {
          data: {
            userID: volunteerToRemove.userID,
          },
          headers: {
            Authorization: `Bearer ${sessionID}`,
          },
          withCredentials: true,
        }
      );
            
      // Refresh the volunteers
      setVolunteers(
        volunteers.filter((volunteer) => volunteer.userID !== volunteerToRemove.userID)
      );
      showNotificationBanner_func('Success', `Successfully checked out ${volunteerToRemove.firstName} ${volunteerToRemove.lastName}`, 'success');
      setShowRemoveConfirmModal(false);
      setVolunteerToRemove(null);
    } catch (error) {
      console.error("Error removing volunteer: ", error);
      const errorMsg = error.response?.data?.message || "Error removing volunteer. Please try again.";
      showNotificationBanner_func('Error', errorMsg, 'error');
    }
  };

  // Checks user role and fetches data
  useEffect(() => {
    const checkUserRoleAndFetchData = async () => {
      const sessionID = Cookies.get("sessionID");
      if (!sessionID) {
        navigate("/SignIn");
        return;
      }

      try {
        const userResponse = await axios.get(
          process.env.REACT_APP_BACKURL + "/api/foodPantry/user/role",
          {
            headers: {
              Authorization: `Bearer ${sessionID}`,
            },
            withCredentials: true,
          }
        );

        const { roleID } = userResponse.data;
        if (roleID === "0") {
          setIsRootUser(true);
          fetchOrganizations(sessionID); // ONLY called for root users
        } else {
          setIsRootUser(false);
          fetchOrgData(); // Called for non-root users
        }
      } catch (error) {
        console.error("Error checking user role: ", error);
        navigate("/SignIn");
      }
    };

    const fetchOrgData = async () => {
      const cookieValue = Cookies.get('OrgID');
      if (!cookieValue) {
        setOrganizationName("No Organization Found in cookie");
        return;
      }

      try {
        const orgId = cookieValue;
        if (orgId) {
          setOrganizationId(orgId);
          const url = process.env.REACT_APP_BACKURL + `/api/foodPantry/organization/${orgId}`;
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include'
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch organization: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          if (data.organization && data.organization.organizationName) {
            setOrganizationName(data.organization.organizationName);
            // For non-root users, only allow their current organization in the dropdown
            setOrganizations([{
              id: orgId,
              name: data.organization.organizationName
            }]);
          } else {
            setOrganizationName("Organization name not available");
          }
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
        setOrganizationName("Error loading organization");
      }
    };

    const fetchOrganizations = async (sessionID) => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_BACKURL + "/api/foodPantry/organization",
          {
            headers: {
              Authorization: `Bearer ${sessionID}`,
            },
            withCredentials: true,
          }
        );
        const data = response.data;
        if (data?.OrgNames && Array.isArray(data.OrgNames)) {
          const formattedOrganizations = data.OrgNames.map((org) => ({
            id: org.orgID,
            name: org.name,
          }));
          setOrganizations(formattedOrganizations);
          setOrganizationName("Select Organization");
        } else {
          console.error("Error fetching organizations: ", data);
        }
      } catch (error) {
        console.error("Error fetching organizations: ", error);
      }
    };

    checkUserRoleAndFetchData();
  }, [navigate]);

  // Gets the volunteers for the selected organization
  useEffect(() => {
    fetchVolunteers(organizationName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationName]);

  // VolunteerTable Component
  const VolunteerTable = ({ volunteers, onRemove }) => {
    const tableData = useMemo(() => {
      return volunteers.map((volunteer) => ({
        ...volunteer,
        volunteerName: `${volunteer.firstName} ${volunteer.lastName}`,
        profilePic: volunteer.profilePic || null,
      }));
    }, [volunteers]);

    const columns = [
      { title: "Name", 
        data: "volunteerName",
        orderable: false, 
        searchable: true 
      },
      {
        title: "Clock Out",
        data: "userID",
        orderable: false,
        searchable: false,
        render: function (data) {
          return `
            <button class="btn btn-sm btn-danger" data-volunteer-id="${data}">
              <i class="bi bi-clock-fill me-1"></i> Clock Out
            </button>
          `;
        },
      },
    ];

    return (
      <DataTable
        data={tableData}
        columns={columns}
        options={{
          paging: true,
          pageLength: 10,
          scrollX: true,
          autoWidth: false,
          searching: true,
          ordering: false,
          responsive: false,
          dom: "frtip",
          language: {
            emptyTable: "No volunteers found.",
            info: "_START_-_END_ of _TOTAL_ volunteers",
            infoEmpty: "0 of 0 volunteers",
            infoFiltered: "(filtered from _MAX_ total volunteers)",
          },
          initComplete: function () {
            // Add click handlers to clock out buttons
            const api = this.api && typeof this.api === "function" ? this.api() : null;
            if (!api) return;

            const container = document.getElementById("manage-volunteers-table-container");
            if (container) {
              container.addEventListener("click", (e) => {
                const btn = e.target.closest("button[data-volunteer-id]");
                if (btn) {
                  const volunteerId = btn.getAttribute("data-volunteer-id");
                  onRemove(volunteerId);
                }
              });
            }
          },
        }}
      />
    );
  };

  return (
    <React.Fragment>
      {/* Toast notification banner */}
      <ToastContainer className="p-5" position="top-end" style={{ zIndex: 9999 }}>
        <Toast
          show={showNotificationBanner}
          onClose={() => setShowNotificationBanner(false)}
          animation={true}
          className={`toast-banner rounded-4 toast-banner-${notificationType.toLowerCase()}`}
        >
          <Toast.Header closeButton>
            <img
              src={
                localStorage.getItem("BurchCoin") === JSON.stringify(true)
                  ? './images/Burchcoin.png'
                  : './images/BridgingHopeNoWord.svg'
              }
              className="rounded me-2"
              alt="Notification Icon"
              style={{ width: '30px', height: '30px' }}
            />
            <strong className="me-auto">{notificationHeader}</strong>
          </Toast.Header>
          <Toast.Body>
            {notificationBody}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header d-flex justify-content-between align-items-center pb-3">
          <h3 className="mb-0">
            {/* Using text-primary for the icon and a darker text color for the title for Bootstrap consistency */}
            <i className="bi bi-person-lines-fill pe-3"></i>
            <span>
              Manage Volunteers
            </span>
          </h3>

          {isRootUser ? (
            <div className="d-flex flex-wrap align-items-center">
              {/* Organization Select for Root User */}
              <select
                className="form-select w-auto me-3" // Added margin-end for spacing
                value={organizationName}
                onChange={(e) => {
                  // Only root users can change organizations
                  if (isRootUser) {
                    setOrganizationName(e.target.value);
                  }
                }}
              >
                <option value="Select Organization">Select Organization</option>
                {organizations.map((org) => (
                  <option key={org.name} value={org.name}>
                    {org.name}
                  </option>
                ))}
              </select>
              {/* Add Volunteer Button - using variant="primary" color */}
              <Button
                variant="primary"
                onClick={() => {
                  resetAddVolunteerModal();
                  fetchUserList();
                  setShowAddVolunteerModal(true);
                }}
                className="mt-2 ms-3"
              >
                <i className="bi bi-person-plus-fill me-1"></i> Check In Volunteer
              </Button>
            </div>
          ) : (
            <div className="d-flex align-items-center p-2 rounded border bg-light">
              <h5 className="mb-0 me-3">
                <span className="fw-bold">Organization:</span> {organizationName}
              </h5>
              {/* Check In Volunteer Button - using variant="primary" color */}
              <Button
                variant="primary"
                onClick={() => {
                  resetAddVolunteerModal();
                  fetchUserList();
                  setShowAddVolunteerModal(true);
                }}
              >
                <i className="bi bi-person-plus-fill me-1"></i> Check In Volunteer
              </Button>
            </div>
          )}
        </div>

        {/* Volunteer Table - Using DataTable */}
        <div id="manage-volunteers-table-container" className="mt-4">
          <style>
  {`
    #manage-volunteers-table-container table.dataTable,
    #manage-volunteers-table-container table.dataTable thead th,
    #manage-volunteers-table-container table.dataTable tbody td,
    #manage-volunteers-table-container .dataTables_wrapper,
    #manage-volunteers-table-container .dataTables_info,
    #manage-volunteers-table-container .dataTables_filter,
    #manage-volunteers-table-container .dataTables_length,
    #manage-volunteers-table-container .dataTables_paginate {
      color: #000 !important;
    }
    
    #manage-volunteers-table-container table.dataTable {
      width: 100% !important;
      table-layout: auto;
    }
    
    #manage-volunteers-table-container table.dataTable thead th {
      white-space: nowrap;
      padding: 0.75rem 0.75rem !important;
      line-height: 1.2 !important;
      vertical-align: middle !important;
    }
    
    #manage-volunteers-table-container table.dataTable thead tr {
      height: auto !important;
    }
    
    #manage-volunteers-table-container table.dataTable tbody td {
      padding: 0.75rem !important;
      vertical-align: middle !important;
    }

    #manage-volunteers-table-container table.dataTable thead .sorting:before,
    #manage-volunteers-table-container table.dataTable thead .sorting:after,
    #manage-volunteers-table-container table.dataTable thead .sorting_asc:before,
    #manage-volunteers-table-container table.dataTable thead .sorting_asc:after,
    #manage-volunteers-table-container table.dataTable thead .sorting_desc:before,
    #manage-volunteers-table-container table.dataTable thead .sorting_desc:after,
    #manage-volunteers-table-container table.dataTable thead .sorting_disabled:before,
    #manage-volunteers-table-container table.dataTable thead .sorting_disabled:after {
      display: none !important;
      content: none !important;
    }
  `}
</style>
          <VolunteerTable 
            volunteers={volunteers} 
            onRemove={handleRemove}
          />
        </div>
      </div>

      {/* Check In Volunteer Modal - Search/Scan first, then form */}
      <Modal
        show={showAddVolunteerModal}
        onHide={() => {
          setShowAddVolunteerModal(false);
          resetAddVolunteerModal();
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="mb-0">
              <i className="bi bi-person-plus-fill me-2"></i>Add New Volunteer
            </h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center d-flex flex-column align-items-center">
          {/* PHASE 1: Identify/Select User */}
          {showIdentifyForm && (
            <>
              {!manualIdentifyForm ? (
                <>
                  {/* QR Code and Search Selection Layout */}
                  <div
                    className={
                      windowWidth < 1000
                        ? "d-flex flex-column align-items-center justify-content-center w-100"
                        : "d-flex flex-row align-items-center justify-content-center w-100"
                    }
                    style={{ minHeight: 350, margin: "0 auto" }}
                  >
                    {/* QR Code Card */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <h5 className="text-center mb-3">QR Code</h5>
                      <div
                        style={{
                          backgroundColor: "white",
                          padding: "30px 20px",
                          borderRadius: "14px",
                          border: "1px solid #ced4da",
                          width: "240px",
                          minHeight: "240px",
                          minWidth: "160px",
                          background: "#f8f9fa",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative"
                        }}
                        className="position-relative"
                      >
                        {loggedInUserId ? (
                          <QRCode
                            value={loggedInUserId}
                            size={200}
                            level="H"
                            includeMargin={false}
                          />
                        ) : (
                          <i className="fa fa-qrcode" style={{ fontSize: "5em", color: "#ccc" }}></i>
                        )}
                      </div>
                      <div className="text-muted mt-3" style={{ fontSize: "0.95em" }}>
                        Scan volunteer QR code or enter ID below
                      </div>
                    </div>

                    {/* Divider with OR */}
                    <div
                      className="d-flex flex-column align-items-center mx-5"
                      style={{ justifyContent: "center" }}
                    >
                      <div style={{
                        background: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "50%",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "8px 0"
                      }}>
                        <span style={{ fontWeight: "550", fontSize: "1.1em" }}>or</span>
                      </div>
                    </div>

                    {/* Manual Search Card */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <h5 className="text-center mb-3">Search</h5>
                      <div
                        style={{
                          padding: "18px 10px",
                          borderRadius: "10px",
                          border: "1px solid #ced4da",
                          width: "240px",
                          minHeight: "240px",
                          minWidth: "240px",
                          background: "#f8f9fa",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                        onClick={() => setManualIdentifyForm(true)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#e9ecef";
                          e.currentTarget.style.borderColor = "#adb5bd";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#f8f9fa";
                          e.currentTarget.style.borderColor = "#ced4da";
                        }}
                      >
                        <i className="bi bi-search mb-2 text-primary" style={{ fontSize: '5em' }}></i>
                      </div>
                      <div className="text-muted mt-3" style={{ fontSize: "0.95em" }}>
                        Search by name or phone
                      </div>
                    </div>
                  </div>

                  {/* Scan input at bottom */}
                  <div className="d-flex justify-content-center mt-4" style={{ maxWidth: 400, margin: "0 auto" }}>
                    <Form.Control
                      type="password"
                      placeholder="Scan or enter volunteer ID"
                      value={userScanCode}
                      onChange={e => setUserScanCode(e.target.value)}
                      onKeyDown={handleUserScanKeyDown}
                      ref={codeInputRef}
                      style={{ padding: "10px", fontSize: "1.1em" }}
                      aria-label="Scan or enter volunteer ID"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Manual Search Form */}
                  <div className="d-flex mb-3 align-items-center w-100" style={{ maxWidth: 400, margin: "0 auto" }}>
                    <Button
                      variant="light"
                      onClick={() => setManualIdentifyForm(false)}
                      className="me-2"
                      aria-label="Back to QR scan"
                    >
                      <i className="fa fa-arrow-left"></i>
                    </Button>
                    <Form className="flex-grow-1">
                      <Form.Control
                        type="search"
                        placeholder="Search by Name or Phone..."
                        aria-label="Search"
                        value={userSearch}
                        onChange={e => {
                          setUserSearch(e.target.value);
                          setUserPage(0);
                        }}
                        ref={searchInputRef}
                      />
                    </Form>
                  </div>

                  {/* Search Results */}
                  {(() => {
                    const trimmedSearch = userSearch.trim();
                    const filteredResults = trimmedSearch.length > 0
                      ? userSearchResults
                        .filter(user =>
                          [
                            user.firstName,
                            user.lastName,
                            user.middleName,
                            user.phoneNumber
                          ]
                            .filter(Boolean)
                            .join(" ")
                            .toLowerCase()
                            .includes(trimmedSearch.toLowerCase())
                        )
                        .sort((a, b) => {
                          const last = (a.lastName || "").localeCompare(b.lastName || "");
                          if (last !== 0) return last;
                          return (a.firstName || "").localeCompare(b.firstName || "");
                        })
                      : [];

                    const pageSize = 8;
                    const pageCount = Math.ceil(filteredResults.length / pageSize);

                    return (
                      <>
                        {filteredResults.length > 0 && (
                          <div
                            className="d-flex flex-column align-items-center w-100"
                            style={{ maxWidth: 400 }}
                          >
                            <ul
                              className="list-group w-100"
                              style={{ maxWidth: 400 }}
                            >
                              {filteredResults
                                .slice(userPage * pageSize, userPage * pageSize + pageSize)
                                .map((user, idx) => (
                                  <li
                                    key={user.userID || idx}
                                    className="list-group-item"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleUserSelect(user)}
                                  >
                                    <strong>
                                      {user.firstName} {user.middleName ? user.middleName + " " : ""}{user.lastName}
                                    </strong>

                                  <br /> {/* Did weird spacing here for readability -Julian */}

                                    <span className='opacity-50'>
                                      {user.phoneNumber && /^\d{10}$/.test(user.phoneNumber)
                                        ? `(${user.phoneNumber.substring(0, 3)}) ${user.phoneNumber.substring(3, 6)}-${user.phoneNumber.substring(6)}`
                                        : user.phoneNumber
                                      }
                                    </span>
                                    <br />
                                  </li>
                                ))}
                              {/* Add empty rows to fill up to 8 */}
                              {Array.from({ length: pageSize - Math.min(pageSize, filteredResults.length - userPage * pageSize) }).map((_, idx) => (
                                <li
                                  key={`empty-${idx}`}
                                  className="list-group-item"
                                  style={{
                                    height: 64.64,
                                    background: "transparent",
                                    border: "transparent"
                                  }}
                                >
                                </li>
                              ))}
                            </ul>
                            {/* Pagination */}
                            {pageCount > 1 && (
                              <div className="d-flex justify-content-between mt-2" style={{ maxWidth: 400, width: "100%" }}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setUserPage(Math.max(0, userPage - 1))}
                                  disabled={userPage === 0}
                                >
                                  Previous
                                </Button>
                                <span>{userPage + 1} / {pageCount}</span>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setUserPage(Math.min(pageCount - 1, userPage + 1))}
                                  disabled={userPage === pageCount - 1}
                                >
                                  Next
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {/* PHASE 2: Add Volunteer Form */}
          {!showIdentifyForm && selectedUserForVolunteer && (
            <>
              <div className="d-flex flex-column align-items-center mb-4">
                <i className="bi bi-exclamation-circle-fill text-warning" style={{ fontSize: "3em", marginBottom: "15px" }}></i>
                <h3 className="mb-0 text-center">
                  <strong>Are you sure you want to add {selectedUserForVolunteer.firstName} {selectedUserForVolunteer.lastName} as a volunteer?</strong>
                </h3>
              </div>
              
              <Button
                variant="light"
                onClick={() => {
                  setShowIdentifyForm(true);
                  setManualIdentifyForm(false);
                }}
                aria-label="Back to search"
                className="position-absolute start-0 m-3"
              >
                <i className="fa fa-arrow-left"></i>
              </Button>

              <Form
                onKeyDown={handleAddKeydown}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddVolunteer();
                }}
              >

                {/* Button moved from Modal.Footer to inside the Form/Body */}
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="submit"
                    variant="primary"
                  >
                    <i className="bi bi-save-fill me-1"></i> Confirm Volunteer Check In
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>

      </Modal>

      <Modal
        show={showEditVolunteerModal}
        onHide={() => setShowEditVolunteerModal(false)}
        size="lg" // Corresponds to modal-lg
        centered // For vertical centering
      >
        <Modal.Header closeButton> {/* React Bootstrap's closeButton adds the default dismiss button */}
          <div className="d-flex align-items-center justify-content-between w-100">

            <Modal.Title className="flex-grow-1">
              <h5 className="mb-0">
                <i className="bi bi-pencil-square me-2"></i>Edit Volunteer
              </h5>
            </Modal.Title>
          </div>
        </Modal.Header>

        {/* --- */}

        <Modal.Body>
          {/* Form is contained in Modal.Body and uses the Form component for React-Bootstrap */}
          <Form
            onKeyDown={handleEditKeydown}
            // The onSubmit handler manages the form action and calls handleSubmit
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="row g-3">

              {/* Smart Input Field: First Name */}
              <div className="col-md-6">
                <div className="form-floating">
                  <Form.Control
                    type="text"
                    id="floatingEditFirstName"
                    placeholder="e.g., Jane"
                    value={editFirstName}
                    onChange={handleEditFirstNameChange}
                    required
                  />
                  <Form.Label htmlFor="floatingEditFirstName">First Name</Form.Label>
                </div>
              </div>

              {/* Smart Input Field: Last Name */}
              <div className="col-md-6">
                <div className="form-floating">
                  <Form.Control
                    type="text"
                    id="floatingEditLastName"
                    placeholder="e.g., Doe"
                    value={editLastName}
                    onChange={handleEditLastNameChange}
                    required
                  />
                  <Form.Label htmlFor="floatingEditLastName">Last Name</Form.Label>
                </div>
              </div>
            </div>

            {/* 1. 🗑️ REMOVED Modal.Footer, 2. MOVED BUTTON INTO FORM */}
            {/* 3. Button type is "submit" to trigger the form's onSubmit handler */}
            <div className="d-flex justify-content-end mt-4">
              <Button
                type="submit" // 👈 CRITICAL: This triggers the Form's onSubmit handler
                variant="primary"
              >
                <i className="bi bi-check-circle-fill me-1"></i> Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        show={showRemoveConfirmModal}
        onHide={() => {
          setShowRemoveConfirmModal(false);
          setVolunteerToRemove(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="mb-0">
              <i className="bi bi-trash-fill me-2 "></i>Clock Out Volunteer
            </h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center d-flex flex-column align-items-center">
          <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: "3em", marginBottom: "15px" }}></i>
          <h5 className="mb-3">
            <strong>Are you sure you want to clock out this volunteer?</strong>
          </h5>
          {volunteerToRemove && (
            <p className="text-muted mb-4">
              <strong>{volunteerToRemove.firstName} {volunteerToRemove.lastName}</strong> will be clocked out.
            </p>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end gap-2">
          <Button
            onClick={handleConfirmRemove}
            className="bg-primary border-primary" // Using primary color for the destructive action to maintain visual consistency with the rest of the app, while still indicating it's a critical action
          >
            <i className="bi bi-trash-fill me-1"></i> Clock Out Volunteer
          </Button>
        </Modal.Footer>
      </Modal>

    </React.Fragment>
  );
};

export default ManageVolunteers;